import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "./cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none shadow-lg hover:shadow-xl",
  {
    variants: {
      variant: {
        // Default button uses accent color via CSS variable set from @lumi/tokens
        default:
          "bg-lumi-accent text-black hover:brightness-110 focus-visible:brightness-125 focus-visible:shadow-none hover:shadow-none px-8 py-3 text-base",
        outline:
          "border border-neutral-700 bg-transparent text-white hover:bg-amber-900/20 hover:text-lumi-accent hover:border-amber-400/60 focus-visible:text-lumi-accent focus-visible:shadow-none hover:shadow-none px-6 py-2",
        ghost:
          "bg-neutral-600/70 hover:bg-neutral-600/90 text-white focus-visible:shadow-none hover:shadow-none px-8 py-3 text-base backdrop-blur-sm",
        // Smaller variants
        compact:
          "bg-lumi-accent text-black hover:brightness-110 focus-visible:brightness-125 focus-visible:shadow-none hover:shadow-none px-4 py-2 text-sm",
        "ghost-sm":
          "bg-transparent hover:text-lumi-accent focus-visible:text-lumi-accent px-3 py-2.5 text-sm shadow-none focus-visible:shadow-none hover:shadow-none",
        // Overlay variant for player controls
        overlay:
          "bg-black/50 hover:bg-black/70 text-white focus-visible:text-lumi-accent p-2 rounded-full shadow-none focus-visible:shadow-none hover:shadow-none",
      },
      size: {
        default: "h-11 px-6",
        sm: "h-9 px-4",
        lg: "h-12 px-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  ),
);
Button.displayName = "Button";
